import React, { useState, useEffect } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { 
  ChevronDownIcon, 
  BellIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  Bars3BottomLeftIcon,
  GlobeAltIcon
} from "@heroicons/react/24/outline";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { logout } from "../../redux/slices/authSlice";
import { fetchLanguages } from "../../redux/slices/languagesSlice";
import { useTranslation } from "../../contexts/LanguageContext";
import Avatar from "./Avatar";

// Tab configuration per page. Only routes that actually filter their data by
// status get tabs; everything else returns [] so the topbar stays clean.
const getTabsForPage = (pathname) => {
  // List pages (e.g. /users, but not /users/:id) only.
  const isUsersList     = pathname === "/users";
  const isAuctionsList  = pathname === "/auctions";
  const isOrdersList    = pathname === "/orders";
  const isWithdrawalsList = pathname === "/withdrawals";

  if (isUsersList) {
    return [
      { key: "topbar.tab.all", value: "all" },
      { key: "topbar.tab.active", value: "active" },
      { key: "topbar.tab.inactive", value: "inactive" },
      { key: "topbar.tab.blocked", value: "blocked" },
    ];
  }

  if (isAuctionsList) {
    return [
      { key: "topbar.tab.all", value: "all" },
      { key: "topbar.tab.live", value: "live" },
      { key: "topbar.tab.upcoming", value: "upcoming" },
      { key: "topbar.tab.completed", value: "completed" },
    ];
  }

  if (isOrdersList) {
    return [
      { key: "topbar.tab.all", value: "all" },
      { key: "topbar.tab.completed", value: "completed" },
      { key: "topbar.tab.paid", value: "paid" },
      { key: "topbar.tab.pending", value: "pending" },
    ];
  }

  if (isWithdrawalsList) {
    return [
      { key: "topbar.tab.all", value: "all" },
      { key: "topbar.tab.completed", value: "completed" },
      { key: "topbar.tab.processing", value: "processing" },
      { key: "topbar.tab.pending", value: "pending" },
    ];
  }

  // Dashboard, user detail, banners, categories, channels, languages,
  // subscriptions, ticketing, transactions (mock), feedbacks, reports etc.
  // — no status tabs.
  return [];
};

// Flag mapping for common language codes
const getFlagForLanguage = (key) => {
  const flagMap = {
    en: "🇺🇸",
    ar: "🇸🇦",
    kw: "🇰🇼",
    fr: "🇫🇷",
    es: "🇪🇸",
    de: "🇩🇪",
    it: "🇮🇹",
    pt: "🇵🇹",
    ru: "🇷🇺",
    zh: "🇨🇳",
    ja: "🇯🇵",
    ko: "🇰🇷",
    hi: "🇮🇳",
    tr: "🇹🇷",
    nl: "🇳🇱",
    pl: "🇵🇱",
    th: "🇹🇭",
    vi: "🇻🇳",
    id: "🇮🇩",
    ms: "🇲🇾",
  };
  return flagMap[key?.toLowerCase()] || "🌐";
};

const userNavigation = [
  { key: "topbar.yourProfile", action: "profile", href: "#" },
  { key: "topbar.settings", action: "settings", href: "#" },
  { key: "topbar.signOut", action: "signout", href: "#" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function TopBar({ activeTab, onTabChange, onMobileMenuClick }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admin = useSelector((state) => state.auth.admin);
  const { languages: apiLanguages, loading: languagesLoading } = useSelector((state) => state.languages);
  const { t, setLanguage, activeLanguageKey } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = getTabsForPage(location.pathname);
  // Source of truth for the active tab is the ?status=... query param so any
  // page in the app can subscribe to it without prop drilling.
  const currentTab = searchParams.get("status") || "all";

  // Fetch languages from API on mount
  useEffect(() => {
    if (apiLanguages.length === 0) {
      dispatch(fetchLanguages({ currentPage: 1, limit: 100 }));
    }
  }, [dispatch, apiLanguages.length]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleTabClick = (tabValue) => {
    const next = new URLSearchParams(searchParams);
    if (!tabValue || tabValue === "all") {
      next.delete("status");
    } else {
      next.set("status", tabValue);
    }
    setSearchParams(next, { replace: true });
    if (onTabChange) onTabChange(tabValue);
  };

  const handleLanguageSelect = (language) => {
    setLanguage(language);
  };

  return (
    <div className="sticky top-0 z-10 flex h-[93px] flex-shrink-0 bg-white shadow-sm border-b border-dashboardStroke">
      {/* Mobile menu button */}
      <button
        type="button"
        className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none md:hidden"
        onClick={onMobileMenuClick}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      
      <div className="flex flex-1 justify-between px-6">
        {/* Left Side - Tabs */}
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabClick(tab.value)}
              className={classNames(
                "text-lg font-normal transition-colors",
                currentTab === tab.value
                  ? "text-dashboardCoral font-semibold underline underline-offset-4"
                  : "text-dashboardDark hover:text-dashboardCoral"
              )}
            >
              {t(tab.key)}
            </button>
          ))}
        </div>

        {/* Right Side - Language, Notifications, Profile */}
        <div className="flex items-center gap-4">
          {/* Notification Bell */}
          <button
            type="button"
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 border border-dashboardStroke hover:bg-gray-200 transition-colors"
          >
            <BellIcon className="h-5 w-5 text-dashboardText" />
          </button>

          {/* Chat/Messages */}
          <button
            type="button"
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 border border-dashboardStroke hover:bg-gray-200 transition-colors"
          >
            <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5 text-dashboardText" />
            {/* Red notification dot */}
            <span className="absolute top-0 end-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>
          </button>

          {/* Language Selector */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 border border-dashboardStroke hover:bg-gray-200 transition-colors overflow-hidden">
              {languagesLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dashboardCoral"></div>
              ) : (
                <span className="text-xl">{activeLanguageKey ? getFlagForLanguage(activeLanguageKey) : <GlobeAltIcon className="h-5 w-5 text-dashboardText" />}</span>
              )}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute end-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {apiLanguages.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    {languagesLoading ? t("topbar.loading") : t("topbar.noLanguages")}
                  </div>
                ) : (
                  apiLanguages.map((language) => (
                    <Menu.Item key={language.key}>
                      {({ active }) => (
                        <button
                          onClick={() => handleLanguageSelect(language)}
                          className={classNames(
                            active ? "bg-gray-100" : "",
                            activeLanguageKey === language.key ? "bg-blue-50 text-blue-700" : "",
                            "flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700"
                          )}
                        >
                          <span className="text-lg">{getFlagForLanguage(language.key)}</span>
                          <span className="flex-1 text-left">{language.name}</span>
                          <span className="text-xs text-gray-400 uppercase">{language.key}</span>
                        </button>
                      )}
                    </Menu.Item>
                  ))
                )}
              </Menu.Items>
            </Transition>
          </Menu>

          {/* Admin Profile */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-3 focus:outline-none">
              {/* Profile Info */}
              <div className="text-right hidden sm:block">
                <p className="text-base font-normal text-dashboardDark">
                  {admin?.name || "Ali Al Husain"}
                </p>
                <p className="text-sm text-dashboardText">
                  {admin?.role || "Admin"}
                </p>
              </div>
              
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                {admin?.profilePicture ? (
                  <img 
                    src={admin.profilePicture} 
                    alt={admin.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Avatar name={admin?.name || "Admin"} size="md" />
                )}
              </div>
              
              {/* Chevron */}
              <ChevronDownIcon className="h-4 w-4 text-dashboardText" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute end-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {userNavigation.map((item) => (
                  <Menu.Item key={item.action}>
                    {({ active }) => (
                      <a
                        href={item.action === "signout" ? undefined : item.href}
                        onClick={item.action === "signout" ? handleLogout : undefined}
                        className={classNames(
                          active ? "bg-gray-100" : "",
                          "block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                        )}
                      >
                        {t(item.key)}
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
}
